import { useState } from "react";
import { Link } from "react-router";
import { TUTORIAL_STEPS, type Onboarding, type TutorialStep } from "@techplanner/shared/core";
import { useAuth } from "../auth";
import { api, errorMessage } from "../lib/api";
import { keys, useOnboarding, useQueryClient } from "../lib/queries";
import { Icon } from "./Icon";
import { Alert, Button, Sheet, useToast } from "./ui";

/**
 * Tutoriel de démarrage de l'admin : une liste d'étapes sur l'accueil, cochées d'après ce
 * qui existe vraiment dans l'application, et une bulle d'aide sur chaque écran concerné.
 */

interface StepInfo {
  title: string;
  text: string;
  action: string;
  /** Écran de l'étape ; l'installation ouvre une fiche d'explications. */
  to: string | null;
  hint: string;
}

export const TUTORIAL: Record<TutorialStep, StepInfo> = {
  branding: {
    title: "Votre application, à vos couleurs",
    text: "Ajoutez votre logo et choisissez votre couleur : vos techniciens ouvriront l'application de votre société.",
    action: "Personnaliser",
    to: "/company",
    hint: "Choisissez une couleur et ajoutez votre logo, puis enregistrez. L'écran de connexion de votre équipe sera à vos couleurs."
  },
  equipment: {
    title: "Vos équipements",
    text: "Chaudières, climatisations, machines… tout le matériel que vous entretenez, avec sa fréquence d'entretien.",
    action: "Ajouter un équipement",
    to: "/equipment-types",
    hint: "Ajoutez chaque modèle que vous entretenez. Ils seront proposés dans les fiches clients, avec leur fréquence d'entretien."
  },
  team: {
    title: "Votre équipe",
    text: "Créez le compte d'un technicien avec son e-mail : il reçoit une invitation pour choisir son mot de passe. Sans e-mail, envoyez-lui le lien par SMS ou WhatsApp.",
    action: "Créer un compte",
    to: "/team",
    hint: "Touchez + pour créer le compte d'un technicien. Saisissez son e-mail pour qu'il reçoive l'invitation ; sans e-mail, envoyez-lui le lien par SMS ou WhatsApp."
  },
  client: {
    title: "Votre premier client",
    text: "Nom, adresse et téléphone : l'itinéraire et l'appel se lancent ensuite d'un geste.",
    action: "Ajouter un client",
    to: "/clients/new",
    hint: "Saisissez le nom, l'adresse et le téléphone. Vous ajouterez ensuite ses équipements depuis sa fiche."
  },
  contract: {
    title: "Votre premier contrat",
    text: "Indiquez la fréquence : tous les passages d'entretien se planifient tout seuls.",
    action: "Créer un contrat",
    to: "/contracts/new",
    hint: "Choisissez le client, l'équipement et la fréquence : l'aperçu montre tous les passages planifiés automatiquement."
  },
  install: {
    title: "L'app sur votre téléphone",
    text: "Ajoutez-la à l'écran d'accueil : elle s'ouvre comme une vraie application, même sans réseau.",
    action: "Voir comment faire",
    to: null,
    hint: ""
  }
};

function useTutorial() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return { isAdmin, onboarding: useOnboarding(isAdmin) };
}

function useTutorialAction() {
  const qc = useQueryClient();
  const toast = useToast();
  return async (action: "skip" | "unskip" | "finish" | "restart", step?: TutorialStep) => {
    try {
      const next = await api<Onboarding>("/onboarding", { method: "POST", body: { action, step } });
      qc.setQueryData(keys.onboarding, next);
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  };
}

/** Société pas encore validée : l'admin peut tout préparer, son équipe attend la validation. */
export function PendingCompanyBanner() {
  const { company, user } = useAuth();
  if (company?.status !== "pending" || user?.role !== "admin") return null;
  return (
    <div className="section">
      <Alert kind="warning" icon="clock">
        <strong>Compte en cours de validation.</strong> Vous pouvez déjà tout mettre en place ; vos techniciens pourront se connecter
        dès que TechPlanner aura validé votre société.
      </Alert>
    </div>
  );
}

/** Liste des étapes sur l'accueil, jusqu'à ce que l'admin la termine. */
export function OnboardingChecklist() {
  const { isAdmin, onboarding } = useTutorial();
  const act = useTutorialAction();
  const [installOpen, setInstallOpen] = useState(false);
  const data = onboarding.data;
  if (!isAdmin || !data || data.finishedAt) return null;

  const settled = data.steps.filter((s) => s.done || s.skipped).length;
  const allSettled = settled === data.steps.length;
  const next = data.steps.find((s) => !s.done && !s.skipped)?.id;

  return (
    <section className="section card card-pad onboarding" aria-label="Mettre en place votre application">
      <div className="row" style={{ gap: 10 }}>
        <div className="grow">
          <h2 className="onboarding-title">{allSettled ? "Bravo, tout est prêt !" : "Mettre en place votre application"}</h2>
          <div className="small muted">
            {settled} sur {data.steps.length} étapes
          </div>
        </div>
        {!allSettled && (
          <button type="button" className="link-button small" onClick={() => void act("finish")}>
            Masquer
          </button>
        )}
      </div>
      <div className="onboarding-progress" role="progressbar" aria-valuemin={0} aria-valuemax={data.steps.length} aria-valuenow={settled}>
        <span style={{ width: `${(settled / data.steps.length) * 100}%` }} />
      </div>

      {allSettled ? (
        <>
          <p className="small text-2">
            Votre application est en place. Vous pourrez revoir ce tutoriel à tout moment depuis Plus.
          </p>
          <Button variant="primary" block icon="check" onClick={() => void act("finish")}>
            Terminer
          </Button>
        </>
      ) : (
        <ol className="onboarding-steps">
          {data.steps.map((s) => {
            const info = TUTORIAL[s.id];
            const isNext = s.id === next;
            return (
              <li key={s.id} className={`onboarding-step${s.done ? " done" : ""}${s.skipped ? " skipped" : ""}${isNext ? " next" : ""}`}>
                <span className="onboarding-check" aria-hidden="true">
                  {s.done ? <Icon name="check" size={16} strokeWidth={3} /> : TUTORIAL_STEPS.indexOf(s.id) + 1}
                </span>
                <div className="grow">
                  <div className="strong">{info.title}</div>
                  {isNext && <p className="small text-2">{info.text}</p>}
                  {s.skipped && (
                    <button type="button" className="link-button small" onClick={() => void act("unskip", s.id)}>
                      Passée · reprendre
                    </button>
                  )}
                  {isNext && (
                    <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                      {info.to ? (
                        <Link to={info.to} className="btn primary small">
                          {info.action}
                        </Link>
                      ) : (
                        <Button variant="primary" size="small" onClick={() => setInstallOpen(true)}>
                          {info.action}
                        </Button>
                      )}
                      <Button variant="ghost" size="small" onClick={() => void act("skip", s.id)}>
                        Passer
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {installOpen && (
        <InstallSheet
          onClose={() => setInstallOpen(false)}
          onDone={() => {
            setInstallOpen(false);
            void act("skip", "install");
          }}
        />
      )}
    </section>
  );
}

/** Bulle d'aide en haut d'un écran, tant que l'étape correspondante du tutoriel n'est pas faite. */
export function TutorialHint({ step }: { step: TutorialStep }) {
  const { isAdmin, onboarding } = useTutorial();
  const data = onboarding.data;
  const state = data?.steps.find((s) => s.id === step);
  if (!isAdmin || !data || data.finishedAt || !state || state.done || state.skipped) return null;
  return (
    <div className="tutorial-hint" role="note">
      <div className="tiny strong" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
        Étape {TUTORIAL_STEPS.indexOf(step) + 1} sur {TUTORIAL_STEPS.length} · {TUTORIAL[step].title}
      </div>
      <p className="small">{TUTORIAL[step].hint}</p>
    </div>
  );
}

function detectPlatform(): "ios" | "android" | "other" {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "other";
}

export function InstallSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const platform = detectPlatform();
  const installed = window.matchMedia?.("(display-mode: standalone)").matches;
  return (
    <Sheet
      open
      title="Installer l'application"
      onClose={onClose}
      footer={
        <Button variant="primary" block icon="check" onClick={onDone}>
          C'est fait
        </Button>
      }
    >
      <div className="form">
        {installed && <Alert kind="info" icon="check">L'application est déjà installée sur cet appareil.</Alert>}
        {(platform === "ios" || platform === "other") && (
          <div>
            <div className="strong">Sur iPhone ou iPad (Safari)</div>
            <ol className="small text-2 install-steps">
              <li>Touchez le bouton Partager (le carré avec une flèche vers le haut).</li>
              <li>Choisissez « Sur l'écran d'accueil ».</li>
              <li>Touchez « Ajouter ».</li>
            </ol>
          </div>
        )}
        {(platform === "android" || platform === "other") && (
          <div>
            <div className="strong">Sur Android (Chrome)</div>
            <ol className="small text-2 install-steps">
              <li>Touchez le menu ⋮ en haut à droite.</li>
              <li>Choisissez « Installer l'application » (ou « Ajouter à l'écran d'accueil »).</li>
              <li>Confirmez.</li>
            </ol>
          </div>
        )}
        <p className="small muted">L'icône apparaît sur l'écran d'accueil ; l'application s'ouvre alors en plein écran, même sans réseau.</p>
      </div>
    </Sheet>
  );
}
